import { type ApiResponse } from '@/types/api';
import {
  ROLE_TYPES,
  type LoginResult,
  type LoginSuccess,
  type MFASetupResponse,
  type RoleType,
  type User,
} from '@/types/auth';
import { apiClient } from '@/lib/api-client';

export interface UpdateMePayload {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  profilePicture?: string;
}

// BE is one-role-per-user (a690359 §4.1). Endpoints return either:
//   - POST /auth/login + /auth/mfa/verify: `user.role: RoleType` (string)
//   - GET  /auth/me:                       `user.role: { id, role: RoleType } | null`
// We flatten both into the FE's singular `User.role: RoleType | null`. The
// id from the /me wrapper is dropped — it's not used anywhere on the FE
// (assignments are managed via PractitionerAssignment, role replacement is
// driven by user id).
function extractRole(raw: Record<string, unknown>): RoleType | null {
  const knownRoles = ROLE_TYPES as readonly string[];
  const role = raw.role;

  if (typeof role === 'string') {
    if (!knownRoles.includes(role)) {
      console.warn('normalizeUser: skipping unknown role', role);
      return null;
    }
    return role as RoleType;
  }

  if (role && typeof role === 'object') {
    const inner = (role as Record<string, unknown>).role;
    if (typeof inner !== 'string' || !knownRoles.includes(inner)) {
      console.warn('normalizeUser: skipping unknown role', inner);
      return null;
    }
    return inner as RoleType;
  }

  return null;
}

function normalizeUser(raw: unknown): User {
  const r = raw as Record<string, unknown>;
  const str = (v: unknown): string => (typeof v === 'string' ? v : '');
  const strOrNull = (v: unknown): string | null => (typeof v === 'string' ? v : null);
  return {
    id: str(r.id),
    email: str(r.email),
    firstName: str(r.firstName),
    lastName: str(r.lastName),
    phoneNumber: strOrNull(r.phoneNumber),
    status:
      r.status === 'ACTIVE' ||
      r.status === 'PENDING_INVITE' ||
      r.status === 'PENDING_VERIFICATION' ||
      r.status === 'DISABLED' ||
      r.status === 'SHADOW'
        ? r.status
        : 'ACTIVE',
    profilePicture: strOrNull(r.profilePicture),
    emailVerified: Boolean(r.emailVerified),
    businessAssociateId: strOrNull(r.businessAssociateId),
    role: extractRole(r),
    // Present only on /auth/me. Left undefined on login/mfa payloads, where the
    // onboarding gate is not consulted.
    onboarded: typeof r.onboarded === 'boolean' ? r.onboarded : undefined,
    // Same /auth/me-only rule as onboarded above. undefined on login/mfa payloads.
    clientType: r.clientType === 'student' || r.clientType === 'corporate' ? r.clientType : null,
    // /auth/me only, and null on every password path (a password login means no
    // SSO by definition). This allow-list is what setUser writes to storage, so
    // omitting the field here would silently drop the marker on every /auth/me
    // and strand federated students on the password form at logout.
    ssoProvider: strOrNull(r.ssoProvider),
    createdAt: str(r.createdAt),
  };
}

export interface MFAVerifyResponse {
  tokens: {
    accessToken: string;
    refreshToken: string;
    idToken: string;
    expiresIn: number;
  };
  user: User;
}

export interface MFASetupVerifyResponse {
  // Backend returns { status, message } with data: null. No tokens.
  message?: string;
}

export interface LogoutResponse {
  message: string;
}

export interface RefreshResponse {
  tokens: {
    accessToken: string;
    idToken: string;
    expiresIn: number;
  };
}

export interface InviteAcceptResponse {
  userId: string;
  email: string;
  message?: string;
}

export interface InviteCheckResponse {
  valid: boolean;
  email: string;
}

export interface ResetPasswordRequestResponse {
  message: string;
}

export interface VerifyEmailResponse {
  message: string;
}

export interface ResendVerificationResponse {
  message: string;
}

export interface ResetPasswordConfirmResponse {
  message: string;
}

export const authApi = {
  async login(email: string, password: string): Promise<ApiResponse<LoginResult>> {
    const response = await apiClient.post<ApiResponse<Record<string, unknown>>>('/auth/login', {
      email,
      password,
    });
    const raw = response.data;
    if (!raw.data) {
      return { ...raw, data: undefined };
    }
    const d = raw.data;

    const narrowed: LoginResult = (() => {
      if (d.mfaRequired === true && typeof d.session === 'string') {
        return { kind: 'mfa_required', session: d.session };
      }
      if (d.mfaSetupRequired === true && typeof d.session === 'string') {
        return { kind: 'mfa_setup_required', session: d.session };
      }
      if (typeof d.tokens === 'object' && d.tokens !== null && typeof d.user === 'object') {
        const tokens = d.tokens as LoginSuccess['tokens'];
        const user = normalizeUser(d.user);
        return { kind: 'success', tokens, user };
      }
      // MSW test handler emits kind directly
      if (d.kind === 'mfa_required' && typeof d.session === 'string') {
        return { kind: 'mfa_required', session: d.session };
      }
      if (d.kind === 'mfa_setup_required' && typeof d.session === 'string') {
        return { kind: 'mfa_setup_required', session: d.session };
      }
      if (d.kind === 'success' && typeof d.tokens === 'object' && typeof d.user === 'object') {
        const tokens = d.tokens as LoginSuccess['tokens'];
        const user = normalizeUser(d.user);
        return { kind: 'success', tokens, user };
      }
      throw new Error('Unrecognised login response shape');
    })();

    return { ...raw, data: narrowed };
  },

  async mfaVerify(
    email: string,
    code: string,
    session: string,
  ): Promise<ApiResponse<MFAVerifyResponse>> {
    const response = await apiClient.post<ApiResponse<MFAVerifyResponse>>('/auth/mfa/verify', {
      email,
      code,
      session,
    });
    const body = response.data;
    if (body.data?.user) {
      body.data.user = normalizeUser(body.data.user);
    }
    return body;
  },

  async mfaSetup(accessToken: string): Promise<ApiResponse<MFASetupResponse>> {
    const response = await apiClient.post<ApiResponse<MFASetupResponse>>(
      '/auth/mfa/setup',
      {},
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return response.data;
  },

  async mfaSetupVerify(
    code: string,
    session: string,
  ): Promise<ApiResponse<MFASetupVerifyResponse | null>> {
    const response = await apiClient.post<ApiResponse<MFASetupVerifyResponse | null>>(
      '/auth/mfa/setup/verify',
      { code },
      { headers: { Authorization: `Bearer ${session}` } },
    );
    return response.data;
  },

  async getMe(): Promise<ApiResponse<User>> {
    const response = await apiClient.get<ApiResponse<User>>('/auth/me');
    const body = response.data;
    if (body.data) body.data = normalizeUser(body.data);
    return body;
  },

  async updateMe(payload: UpdateMePayload): Promise<ApiResponse<User>> {
    const response = await apiClient.patch<ApiResponse<User>>('/auth/me', payload);
    const body = response.data;
    if (body.data) body.data = normalizeUser(body.data);
    return body;
  },

  async logout(refreshToken?: string): Promise<ApiResponse<LogoutResponse>> {
    const response = await apiClient.post<ApiResponse<LogoutResponse>>('/auth/logout', {
      refreshToken,
    });
    return response.data;
  },

  async refresh(refreshToken: string): Promise<ApiResponse<RefreshResponse>> {
    const response = await apiClient.post<ApiResponse<RefreshResponse>>('/auth/refresh', {
      refreshToken,
    });
    return response.data;
  },

  async inviteAccept(token: string, password: string): Promise<ApiResponse<InviteAcceptResponse>> {
    const response = await apiClient.post<ApiResponse<InviteAcceptResponse>>(
      '/auth/invite/accept',
      { token, password },
    );
    return response.data;
  },

  /** Validate an invite token without consuming it (accept page calls this on load). */
  async inviteCheck(token: string): Promise<ApiResponse<InviteCheckResponse>> {
    const response = await apiClient.post<ApiResponse<InviteCheckResponse>>('/auth/invite/check', {
      token,
    });
    return response.data;
  },

  async resetPasswordRequest(email: string): Promise<ApiResponse<ResetPasswordRequestResponse>> {
    const response = await apiClient.post<ApiResponse<ResetPasswordRequestResponse>>(
      '/auth/reset-password/request',
      { email },
    );
    return response.data;
  },

  async resetPasswordConfirm(
    token: string,
    password: string,
  ): Promise<ApiResponse<ResetPasswordConfirmResponse>> {
    const response = await apiClient.post<ApiResponse<ResetPasswordConfirmResponse>>(
      '/auth/reset-password/confirm',
      { token, password },
    );
    return response.data;
  },

  async verifyEmail(token: string): Promise<ApiResponse<VerifyEmailResponse>> {
    const response = await apiClient.post<ApiResponse<VerifyEmailResponse>>('/auth/verify-email', {
      token,
    });
    return response.data;
  },

  async resendVerification(email: string): Promise<ApiResponse<ResendVerificationResponse>> {
    const response = await apiClient.post<ApiResponse<ResendVerificationResponse>>(
      '/auth/resend-verification',
      { email },
    );
    return response.data;
  },

  async registerClient(payload: {
    slug: string;
    email: string;
    password: string;
  }): Promise<ApiResponse<{ userId: string; organizationId: string; locationId: string }>> {
    const response = await apiClient.post<
      ApiResponse<{ userId: string; organizationId: string; locationId: string }>
    >('/clients/register', payload);
    return response.data;
  },
};
